import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Check, Search } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { TaskPerson, TaskPriority, TaskRepeat, TaskStatus } from "../../../services/taskService";
import {
    addDays,
    dateToDay,
    dayToDate,
    formatDay,
    ordinal,
    personName,
    PRIORITIES,
    PRIORITY_META,
    STATUS_META,
    STATUSES,
    weekdayOf,
} from "../taskUtils";
import { useTaskStore } from "../TaskContext";
import { PriorityFlag, StaffAvatar, StatusDot } from "./TaskBits";

type Anchor = HTMLElement | null;

// ── Assignee ────────────────────────────────────────────────────────────────

interface PeopleMenuProps {
    anchorEl: Anchor;
    onClose: () => void;
    value: string | null;
    onSelect: (userId: string | null) => void;
    allowUnassigned?: boolean;
    /** People to list; defaults to everyone who can be given tasks. */
    people?: TaskPerson[];
}

/** Type to narrow, ↑ ↓ to move, Enter to pick. Shows each person's open
 * load so work is spread fairly. */
export const PeopleMenu: React.FC<PeopleMenuProps> = ({ anchorEl, onClose, value, onSelect, allowUnassigned = true, people }) => {
    const store = useTaskStore();
    const list = people ?? store.assignable;
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const open = !!anchorEl;

    useEffect(() => {
        if (open) {
            setQuery("");
            setActive(0);
        }
    }, [open]);

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        const people = list.filter((p) => !q || personName(p).toLowerCase().includes(q) || p.email.toLowerCase().includes(q));
        const rows: { id: string | null; person?: TaskPerson }[] = people.map((p) => ({ id: p.user_id, person: p }));
        if (allowUnassigned && (!q || "unassigned nobody".includes(q))) rows.push({ id: null });
        return rows;
    }, [list, query, allowUnassigned]);

    const pick = (id: string | null) => {
        onSelect(id);
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 300, maxHeight: 420, display: "flex", flexDirection: "column" } } }}
        >
            <Box sx={{ p: 1 }}>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Find a person"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActive(0);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setActive((i) => Math.min(i + 1, options.length - 1));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActive((i) => Math.max(i - 1, 0));
                        } else if (e.key === "Enter" && options[active]) {
                            e.preventDefault();
                            pick(options[active].id);
                        }
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>
            <Box sx={{ overflowY: "auto", pb: 0.5 }}>
                {options.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                        No one matches “{query}”.
                    </Typography>
                )}
                {options.map((o, i) => (
                    <MenuItem
                        key={o.id ?? "none"}
                        selected={i === active}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => pick(o.id)}
                        sx={{ gap: 1.25, py: 0.75 }}
                    >
                        <StaffAvatar person={o.person} size={28} tooltip={false} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                {o.person ? personName(o.person) : "Unassigned"}
                                {o.person && o.id === store.meId && (
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {" "}
                                        (you)
                                    </Typography>
                                )}
                            </Typography>
                            {o.person && (
                                <Typography variant="caption" color={o.person.overdue_count ? "error.main" : "text.secondary"}>
                                    {o.person.open_count === 0
                                        ? "Nothing open"
                                        : `${o.person.open_count} open${o.person.overdue_count ? ` · ${o.person.overdue_count} overdue` : ""}`}
                                </Typography>
                            )}
                        </Box>
                        {o.id === value && <Check fontSize="small" color="primary" />}
                    </MenuItem>
                ))}
            </Box>
        </Popover>
    );
};

// ── Status & priority ───────────────────────────────────────────────────────

interface ChoiceMenuProps<T> {
    anchorEl: Anchor;
    onClose: () => void;
    value: T;
    onSelect: (value: T) => void;
}

export const StatusMenu: React.FC<ChoiceMenuProps<TaskStatus>> = ({ anchorEl, onClose, value, onSelect }) => (
    <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={onClose} slotProps={{ paper: { sx: { minWidth: 220 } } }}>
        {STATUSES.map((s, i) => (
            <MenuItem
                key={s}
                autoFocus={s === value}
                selected={s === value}
                onClick={() => {
                    onSelect(s);
                    onClose();
                }}
            >
                <ListItemIcon>
                    <StatusDot status={s} size={10} />
                </ListItemIcon>
                <ListItemText primary={STATUS_META[s].label} secondary={STATUS_META[s].hint} />
                <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    {i + 1}
                </Typography>
            </MenuItem>
        ))}
    </Menu>
);

export const PriorityMenu: React.FC<ChoiceMenuProps<TaskPriority>> = ({ anchorEl, onClose, value, onSelect }) => (
    <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={onClose} slotProps={{ paper: { sx: { minWidth: 180 } } }}>
        {PRIORITIES.map((p) => (
            <MenuItem
                key={p}
                autoFocus={p === value}
                selected={p === value}
                onClick={() => {
                    onSelect(p);
                    onClose();
                }}
            >
                <ListItemIcon>
                    <PriorityFlag priority={p} />
                </ListItemIcon>
                <ListItemText primary={PRIORITY_META[p].label} />
            </MenuItem>
        ))}
    </Menu>
);

// ── Due date ────────────────────────────────────────────────────────────────

interface DueMenuProps {
    anchorEl: Anchor;
    onClose: () => void;
    date: string | null;
    time: string | null;
    today: string;
    onSelect: (date: string | null, time: string | null) => void;
}

/** Next Saturday / Monday from a day (never the day itself). */
const nextWeekday = (from: string, weekday: number) => {
    const diff = (weekday - weekdayOf(from) + 7) % 7 || 7;
    return addDays(from, diff);
};

export const DueMenu: React.FC<DueMenuProps> = ({ anchorEl, onClose, date, time, today, onSelect }) => {
    const [timeText, setTimeText] = useState("");
    useEffect(() => {
        if (anchorEl) setTimeText(time ? time.slice(0, 5) : "");
    }, [anchorEl, time]);

    const quick: { label: string; day: string }[] = [
        { label: "Today", day: today },
        { label: "Tomorrow", day: addDays(today, 1) },
        { label: "Next Monday", day: nextWeekday(today, 1) },
        { label: "In a week", day: addDays(today, 7) },
    ];
    const withTime = () => (timeText ? `${timeText}:00` : null);

    return (
        <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 320 } } }}
        >
            <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ p: 1.5, pb: 0.5 }}>
                {quick.map((q) => (
                    <Button
                        key={q.label}
                        size="small"
                        variant={date === q.day ? "contained" : "outlined"}
                        onClick={() => {
                            onSelect(q.day, withTime());
                            onClose();
                        }}
                        sx={{ textTransform: "none", borderRadius: 99, py: 0.25 }}
                    >
                        {q.label}
                        <Typography component="span" variant="caption" sx={{ ml: 0.75, opacity: 0.7 }}>
                            {formatDay(q.day, today) === q.label ? "" : formatDay(q.day, today)}
                        </Typography>
                    </Button>
                ))}
            </Stack>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                    value={date ? dayToDate(date) : null}
                    onChange={(d: Date | null) => {
                        if (!d) return;
                        onSelect(dateToDay(d), withTime());
                        onClose();
                    }}
                    sx={{ width: "100%", height: 300, "& .MuiPickersCalendarHeader-root": { mt: 0.5 } }}
                />
            </LocalizationProvider>
            <Divider />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
                <TextField
                    size="small"
                    type="time"
                    label="Time (optional)"
                    value={timeText}
                    onChange={(e) => setTimeText(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ flex: 1 }}
                />
                {date && (
                    <Button
                        size="small"
                        onClick={() => {
                            onSelect(date, withTime());
                            onClose();
                        }}
                    >
                        Set time
                    </Button>
                )}
                {date && (
                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                            onSelect(null, null);
                            onClose();
                        }}
                    >
                        No date
                    </Button>
                )}
            </Stack>
        </Popover>
    );
};

// ── Labels ──────────────────────────────────────────────────────────────────

interface LabelsMenuProps {
    anchorEl: Anchor;
    onClose: () => void;
    value: string[];
    onChange: (labels: string[]) => void;
}

export const LabelsMenu: React.FC<LabelsMenuProps> = ({ anchorEl, onClose, value, onChange }) => {
    const { labels } = useTaskStore();
    const [text, setText] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (anchorEl) setText("");
    }, [anchorEl]);

    const has = (l: string) => value.some((v) => v.toLowerCase() === l.toLowerCase());
    const toggle = (l: string) => onChange(has(l) ? value.filter((v) => v.toLowerCase() !== l.toLowerCase()) : [...value, l]);
    const typed = text.trim().replace(/^#/, "");
    const suggestions = Array.from(new Set([...value, ...labels])).filter(
        (l) => !typed || l.toLowerCase().includes(typed.toLowerCase()),
    );
    const canCreate = typed && !suggestions.some((l) => l.toLowerCase() === typed.toLowerCase());

    return (
        <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 260, maxHeight: 380 } } }}
        >
            <Box sx={{ p: 1 }}>
                <TextField
                    inputRef={inputRef}
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Add a label"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && typed) {
                            e.preventDefault();
                            const existing = suggestions.find((l) => l.toLowerCase() === typed.toLowerCase());
                            if (existing) {
                                if (!has(existing)) toggle(existing);
                            } else if (typed.length <= 30) {
                                onChange([...value, typed]);
                            }
                            setText("");
                        }
                    }}
                    helperText={typed.length > 30 ? "Labels are 30 characters at most" : undefined}
                    error={typed.length > 30}
                />
            </Box>
            {canCreate && typed.length <= 30 && (
                <MenuItem
                    onClick={() => {
                        onChange([...value, typed]);
                        setText("");
                        inputRef.current?.focus();
                    }}
                >
                    <Typography variant="body2">
                        Create <b>#{typed}</b>
                    </Typography>
                </MenuItem>
            )}
            {suggestions.map((l) => (
                <MenuItem key={l} dense onClick={() => toggle(l)}>
                    <Checkbox size="small" checked={has(l)} sx={{ p: 0.5, mr: 1 }} />
                    <Typography variant="body2">#{l}</Typography>
                </MenuItem>
            ))}
            {!canCreate && suggestions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>
                    Type a label and press Enter.
                </Typography>
            )}
        </Popover>
    );
};

// ── Repeat ──────────────────────────────────────────────────────────────────

interface RepeatMenuProps {
    anchorEl: Anchor;
    onClose: () => void;
    rule: TaskRepeat | null;
    interval: number;
    dueDate: string | null;
    today: string;
    onSelect: (rule: TaskRepeat | null, interval: number) => void;
}

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const UNIT: Record<TaskRepeat, [string, string]> = { DAILY: ["day", "days"], WEEKLY: ["week", "weeks"], MONTHLY: ["month", "months"] };

export const RepeatMenu: React.FC<RepeatMenuProps> = ({ anchorEl, onClose, rule, interval, dueDate, today, onSelect }) => {
    const base = dueDate || today;
    const [customRule, setCustomRule] = useState<TaskRepeat>(rule || "DAILY");
    const [customN, setCustomN] = useState(String(interval > 1 ? interval : 2));
    useEffect(() => {
        if (anchorEl) {
            setCustomRule(rule || "DAILY");
            setCustomN(String(interval > 1 ? interval : 2));
        }
    }, [anchorEl, rule, interval]);

    const options: { rule: TaskRepeat | null; n: number; label: string }[] = [
        { rule: null, n: 1, label: "Doesn't repeat" },
        { rule: "DAILY", n: 1, label: "Every day" },
        { rule: "WEEKLY", n: 1, label: `Every week on ${WEEKDAY_NAMES[weekdayOf(base)]}` },
        { rule: "MONTHLY", n: 1, label: `Every month on the ${ordinal(Number(base.slice(8, 10)))}` },
    ];
    const n = Math.min(365, Math.max(1, Number(customN) || 1));
    const choose = (r: TaskRepeat | null, i: number) => {
        onSelect(r, i);
        onClose();
    };

    return (
        <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 290 } } }}
        >
            {options.map((o) => (
                <MenuItem key={o.label} selected={o.rule === rule && (o.rule === null || interval === o.n)} onClick={() => choose(o.rule, o.n)}>
                    {o.label}
                </MenuItem>
            ))}
            <Divider />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5 }}>
                <Typography variant="body2">Every</Typography>
                <TextField
                    size="small"
                    type="number"
                    value={customN}
                    onChange={(e) => setCustomN(e.target.value)}
                    slotProps={{ htmlInput: { min: 1, max: 365 } }}
                    sx={{ width: 70 }}
                />
                <TextField
                    select
                    size="small"
                    value={customRule}
                    onChange={(e) => setCustomRule(e.target.value as TaskRepeat)}
                    sx={{ flex: 1 }}
                >
                    {(Object.keys(UNIT) as TaskRepeat[]).map((r) => (
                        <MenuItem key={r} value={r}>
                            {UNIT[r][n === 1 ? 0 : 1]}
                        </MenuItem>
                    ))}
                </TextField>
                <Button size="small" variant="contained" onClick={() => choose(customRule, n)}>
                    Set
                </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, pb: 1.5 }}>
                Finishing one adds the next, dated after today — missed ones don't pile up.
            </Typography>
        </Popover>
    );
};
